import { Head, usePage } from "@inertiajs/react"

import { StaffLayout } from "@/components/layouts/staff-layout"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { NuqsInertiaAdapter } from "@/features/staff/nuqs-inertia-adapter"
import { StatsEmptyState } from "@/features/staff/stats/empty-state"
import { StatsHourMap } from "@/features/staff/stats/hour-map"
import { StatsKpiBand } from "@/features/staff/stats/kpi-band"
import { StatsLeaksBlock } from "@/features/staff/stats/leaks-block"
import { StatsMixBlock } from "@/features/staff/stats/mix-block"
import { StatsRangeControl } from "@/features/staff/stats/range-control"
import { StatsSeriesChart } from "@/features/staff/stats/series-chart"
import { tStats } from "@/features/staff/stats/format"
import type { StatsPageProps } from "@/features/staff/stats/types"

/**
 * Estadísticas.
 *
 * The question this screen answers is "¿cómo venimos?". `/reports` already
 * answers "¿cuánto cerró el periodo?" and reconciles to the centavo with the
 * signed Z reports inside it; this one reads the SAME base — closed shifts,
 * through `Staff::ReportPeriod` — to answer the other one. They are not two ways
 * of counting money, which is exactly why a manager can hold both on screen.
 *
 * The blocks are in the order the questions get asked:
 *
 *   Ventas netas        the headline and its trend.
 *   Cuándo vende        hour by weekday. The staffing map, and the biggest
 *                       controllable cost in the building.
 *   Fugas               discounts, cancellations, refunds, reopenings.
 *   Mix                 which dishes carry the menu.
 *
 * Everything here is composition. Every figure was computed server-side in
 * integer cents and arrives as an exact decimal string; this page formats and
 * arranges, and derives nothing.
 */
export default function StaffLocationManageStatsShow() {
  const { stats, ranges, hasClosedShifts } = usePage<StatsPageProps>().props
  const { range } = stats

  return (
    // The adapter has to sit above anything reading the range.
    <NuqsInertiaAdapter>
      <Head title={tStats("title")} />

      {/* `md` — the operate-surface width every management screen shares. */}
      <MainContainer size="md">
        <div className="space-y-6" data-testid="stats-screen">
          <PageHeader title={tStats("title")} description={tStats("description")} />

          <StatsRangeControl range={range} options={ranges} />

          {range.empty ? (
            <StatsEmptyState hasClosedShifts={hasClosedShifts} />
          ) : (
            <>
              <StatsKpiBand stats={stats} />
              <StatsSeriesChart
                series={stats.series}
                granularity={range.granularity}
                averagePerBucket={stats.sales.averagePerBucket}
              />
              <StatsHourMap
                cells={stats.weekdayHours}
                granularity={range.granularity}
                closedBucketCount={range.closedBucketCount}
              />
              <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
                <StatsLeaksBlock leaks={stats.leaks} />
                <StatsMixBlock mix={stats.mix} />
              </div>
            </>
          )}
        </div>
      </MainContainer>
    </NuqsInertiaAdapter>
  )
}

StaffLocationManageStatsShow.layout = (page: React.ReactNode) => (
  <StaffLayout>{page}</StaffLayout>
)
